import type { ReactNode } from 'react';
import { memo } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { TextBlock, Heading } from '@library';
import CurrentStoreLink from '../../../core/components/router/CurrentStoreLink';
import useTabletView from '../../../core/hooks/dom/useTabletView';
import useLoginState from '../../../core/hooks/useLoginState';
import type { ProductItem, ProductWithRelatedData } from '../../types';
import stripHtml from '../../../core/util/stripHtml';
import useProductView from '../../hooks/useProductView';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import ProductTileSKUInfo from './ProductTileSKUInfo';
import ProductAttributeSelector from './ProductAttributeSelector';
import ProductTileCarousel from './ProductTileCarousel';
import ProductTileTag from './ProductTileTag';
import ProductTileAvailability from './ProductTileAvailability';
import ProductTilePricingAndActions from './ProductTilePricingAndActions';
import styles from './ProductTile.module.scss';

interface BaseProps {
    stretch?: boolean;
    renderInstantly?: boolean;
    variantExistsInList?: boolean;
    fullHeight?: boolean;
    reduced?: boolean;
}

type ReducedProduct = Pick<
    ProductItem,
    | 'name'
    | 'id'
    | 'isNew'
    | 'onSale'
    | 'manufacturer'
    | 'tileAttributes'
    | 'media'
>;

export type ReducedProductTileProps = BaseProps & {
    reduced: true;
    product: ReducedProduct;
};

type ProductTileProps =
    | (BaseProps & {
          product: ProductWithRelatedData;
      })
    | ReducedProductTileProps;

const ProductTileDetailsContainer = memo(
    ({
        variantExistsInList,
        children,
    }: {
        variantExistsInList?: boolean;
        children: ReactNode;
    }) => {
        const isLoggedIn = useLoginState();
        return (
            <div
                className={clsx(styles.detailsContainer, {
                    [styles.withVariant]: variantExistsInList,
                    [styles.loggedInView]: isLoggedIn,
                    [styles.withVariantAndLoggedIn]:
                        variantExistsInList && isLoggedIn,
                })}
            >
                {children}
            </div>
        );
    }
);

const ProductTile = memo((props: ProductTileProps) => {
    const isTabletView = useTabletView();
    const {
        product,
        stretch = false,
        renderInstantly = false,
        variantExistsInList,
        fullHeight = false,
        ...ariaProps
    } = props;

    const {
        isShow,
        ref,
        activeSKU,
        setActiveSKU,
        isSimpleProduct,
        productDetailsLink,
        startingPrice,
        customerPrice,
    } = useProductView({
        product,
        renderInstantly,
    });

    const {
        manufacturer,
        name,
        tileAttributes: tileAttributesOrNull,
    } = product;

    const tileAttributes = tileAttributesOrNull || [];

    return (
        <div
            className={clsx(styles.productTile, {
                [styles.productTileStretch]: stretch,
                [styles.fullHeight]: fullHeight,
            })}
            ref={ref}
            {...ariaProps}
        >
            {isShow && (
                <>
                    <div className={styles.basicInfoContainer}>
                        <CurrentStoreLink
                            to={productDetailsLink}
                            className={styles.topBar}
                        >
                            <div className={styles.newIndicatorBox}>
                                <ProductTileTag product={product} />
                            </div>
                            <TextBlock
                                tertiary={!isTabletView}
                                secondary={isTabletView}
                                medium
                                center
                                uppercase
                            >
                                <span className={styles.brandTitle}>
                                    {manufacturer}
                                </span>
                            </TextBlock>
                            <div
                                className={styles.productAvailabilityBox}
                                data-test-class="productAvailabilityBox"
                            >
                                {!props.reduced && (
                                    <ProductTileAvailability
                                        hasVariants={!isSimpleProduct}
                                        skuList={props.product.variants.map(
                                            item => item.sku
                                        )}
                                        activeSKU={activeSKU}
                                    />
                                )}
                            </div>
                        </CurrentStoreLink>
                        <div className={styles.productImageContainer}>
                            <ProductTileCarousel
                                productDetailsLink={productDetailsLink}
                                media={product.media}
                            />
                        </div>
                        {(!props.reduced || activeSKU) && (
                            <ProductTileSKUInfo activeSKU={activeSKU} />
                        )}
                        <CurrentStoreLink
                            data-test-class="productDetailsLink"
                            className={styles.productTitle}
                            to={productDetailsLink}
                        >
                            <Heading
                                h6={!isTabletView}
                                h5={isTabletView}
                                center
                            >
                                <span data-test-class="productName">
                                    {name}
                                </span>
                            </Heading>
                        </CurrentStoreLink>
                        <TextBlock caption center uppercase>
                            {tileAttributes.length > 0 ? (
                                stripHtml(tileAttributes[0].value)
                            ) : (
                                <span className={styles.infoSpace} />
                            )}
                        </TextBlock>
                    </div>
                    {!props.reduced && (
                        <ProductTileDetailsContainer
                            variantExistsInList={variantExistsInList}
                        >
                            {props.product.variants.length > 0 && (
                                <div className={styles.attributesContainer}>
                                    <ProductAttributeSelector
                                        variants={props.product.variants}
                                        onAttributeSelected={setActiveSKU}
                                        selectedVariantSKU={activeSKU}
                                    />
                                </div>
                            )}
                            <ProductTilePricingAndActions
                                product={props.product}
                                selectedSKU={activeSKU}
                                variantStartingPrice={startingPrice}
                                customerPrice={customerPrice}
                            />
                        </ProductTileDetailsContainer>
                    )}
                </>
            )}
        </div>
    );
});

export default memo(
    withErrorBoundary(ProductTile, {
        customMessage: <FormattedMessage id="Product can't be displayed" />,
    })
);
